import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import formatOfferInclusionDescription from 'lib/offer/formatOfferInclusionDescription'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import CheckboxButton from 'LuxKit/Checkbox/CheckboxButton'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'

const Container = styled.div`
  margin-top: ${rem(12)};
`

const TicketText = styled(BodyTextBlock)`
  flex: 1;
  min-width: 0;
`

interface OptionalBundleTicketTextProps {
  ticket: App.Property.OptionalBundleTicket
  /** The bundle's total price, rendered inline after the description on one designated line. */
  price?: React.ReactNode
}

/** One line per ticket, icon + description; the bundle's price renders inline on the last line. */
function OptionalBundleTicketText({ ticket, price }: OptionalBundleTicketTextProps) {
  const intl = useIntl()
  const { inclusion } = ticket
  const Icon = useMemo(() => getOfferFeatureIcon(inclusion.symbol), [inclusion.symbol])
  const description = useMemo(
    () => formatOfferInclusionDescription(inclusion.description, intl),
    [inclusion.description, intl],
  )

  return <TicketText
    variant="medium"
    startIcon={<Icon data-symbol={inclusion.symbol} />}
  >
    {description}
    {price && <> <BodyText as="span" variant="medium" weight="bold" wrap="no-wrap">{price}</BodyText></>}
  </TicketText>
}

interface Props {
  optionalBundles: Array<App.Property.OptionalBundle>
  checkedBundleIds: Set<string>
  onToggleBundle: (bundleId: string, event: React.ChangeEvent<HTMLInputElement>) => void
}

interface OptionalBundleRowProps {
  optionalBundle: App.Property.OptionalBundle
  checked: boolean
  onToggleBundle: Props['onToggleBundle']
}

function PropertyOptionalBundleRow({ optionalBundle, checked, onToggleBundle }: OptionalBundleRowProps) {
  const intl = useIntl()

  const onCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onToggleBundle(optionalBundle.bundleId, event)
  }, [onToggleBundle, optionalBundle.bundleId])

  const checkboxLabel = useMemo(
    () =>
      optionalBundle.tickets
        .map(ticket => formatOfferInclusionDescription(ticket.inclusion.description, intl))
        .join(', '),
    [optionalBundle.tickets, intl],
  )

  const lastTicketIndex = optionalBundle.tickets.length - 1

  return <Pane background="highlight-primary" borderRadius="S" padding="12 16">
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
      <Group direction="vertical" gap={4}>
        {optionalBundle.tickets.map((ticket, index) => <OptionalBundleTicketText
          key={`${ticket.experienceId}-${ticket.productId}`}
          ticket={ticket}
          price={index === lastTicketIndex
            ? <FormatCurrency value={optionalBundle.totalPrice} format="roundedDollar" signDisplay="always" />
            : undefined}
        />)}
      </Group>
      <CheckboxButton
        checked={checked}
        onChange={onCheckboxChange}
        size="medium"
        aria-label={checkboxLabel}
      />
    </Group>
  </Pane>
}

/**
 * Customer-selectable bundles for a single rate. Ticking one adds its tickets to the checkout and
 * its `totalPrice` to the tile's displayed total — the rate price itself is hotel-only.
 */
function PropertyOptionalBundleOption({ optionalBundles, checkedBundleIds, onToggleBundle }: Props) {
  if (!optionalBundles.length) return null

  return <Container>
    <Group direction="vertical" gap={8}>
      <BodyText variant="medium" weight="bold">
        <FormattedMessage
          id="wPN3aFmoFy3Xsdo2Gqht"
          defaultMessage="Extras to elevate your stay"
          description="Heading above the customer-selectable add-on bundles on a hotel rate"
        />
      </BodyText>
      {optionalBundles.map(optionalBundle => <PropertyOptionalBundleRow
        key={optionalBundle.bundleId}
        optionalBundle={optionalBundle}
        checked={checkedBundleIds.has(optionalBundle.bundleId)}
        onToggleBundle={onToggleBundle}
      />)}
    </Group>
  </Container>
}

export default PropertyOptionalBundleOption
